import React, { createContext, useContext, useState, ReactNode } from 'react';
import { cn } from '../../utils/cn';

// Types
interface TabsContextType {
  activeTab: string;
  setActiveTab: (tabId: string) => void;
}

interface TabsProps {
  defaultValue: string;
  className?: string;
  children: ReactNode;
  onValueChange?: (value: string) => void;
}

interface TabsListProps {
  className?: string;
  children: ReactNode;
}

interface TabsTriggerProps {
  value: string;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
}

interface TabsContentProps {
  value: string;
  className?: string;
  children: ReactNode;
}

// Context
const TabsContext = createContext<TabsContextType | null>(null);

// Hook pour utiliser le contexte
const useTabs = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs must be used within a Tabs component');
  }
  return context;
};

// Composant principal Tabs
export const Tabs: React.FC<TabsProps> = ({ 
  defaultValue, 
  className, 
  children, 
  onValueChange 
}) => {
  const [activeTab, setActiveTab] = useState(defaultValue);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onValueChange?.(tabId);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

// Liste des onglets - Style simple avec ligne en dessous
export const TabsList: React.FC<TabsListProps> = ({ className, children }) => {
  return (
    <div className={cn(
      'flex items-center border-b border-gray-200 overflow-x-auto scrollbar-hide',
      className
    )}>
      {children}
    </div>
  );
};

// Déclencheur d'onglet - Style simple : nom + ligne en dessous
export const TabsTrigger: React.FC<TabsTriggerProps> = ({ 
  value, 
  className, 
  children, 
  disabled = false 
}) => {
  const { activeTab, setActiveTab } = useTabs();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      onClick={() => !disabled && setActiveTab(value)}
      disabled={disabled}
      className={cn(
        'px-3 py-3 sm:px-4 text-sm font-medium transition-colors duration-200 relative whitespace-nowrap flex-shrink-0',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
        'disabled:pointer-events-none disabled:opacity-50',
        isActive 
          ? 'text-primary border-b-2 border-primary' 
          : 'text-gray-500 hover:text-gray-700',
        className
      )}
    >
      {children}
    </button>
  );
};

// Contenu des onglets
export const TabsContent: React.FC<TabsContentProps> = ({ 
  value, 
  className, 
  children 
}) => {
  const { activeTab } = useTabs();
  
  if (activeTab !== value) {
    return null;
  }

  return (
    <div 
      className={cn(
        'mt-6 focus:outline-none',
        'animate-in fade-in-0 duration-200',
        className
      )}
    >
      {children}
    </div>
  );
};